import { useCallback, useEffect, useRef } from 'react';
import type { BriefSectionResponseDto } from '@inference.engine/workspace-api-client/web';
import { EditorContent, type Editor } from '@tiptap/react';
import { BRIEF_PROSE_CLASSES, useBriefSectionViewer } from '@/entities/brief';
import {
  briefSectionHighlights,
  getEditingCommentId,
  usePendingTextAnchor,
  useSelectedCommentId,
  useSelectionActions,
} from '@/features/comment-on-file';
import type { BriefCommentAnnotation } from '@/features/comment-on-file';
import { getCommentHighlightRanges } from '@/shared/lib/tiptap';
import { BriefSectionError } from '../BriefSectionError';
import { BriefSectionFormatBar } from '../BriefSectionFormatBar';
import { useProjectCapabilities } from '@/entities/user';
import { useMountEffect } from '@/shared/hooks/useMountEffect';
import { useReducedMotion } from 'motion/react';

interface BriefSectionEditorProps {
  section: BriefSectionResponseDto;
  onChange: (sectionId: string, content: unknown) => void;
  onEditorFocus?: (editor: Editor) => void;
  /**
   * Registers/unregisters this section's live editor instance with the page so
   * an agent suggestion can be applied into it as an undoable edit. Called with
   * the editor on mount and `null` on unmount.
   */
  registerEditor?: (sectionId: string, editor: Editor | null) => void;
  onAddCommentInitiated?: () => void;
  onHighlightClick?: () => void;
  annotations?: BriefCommentAnnotation[];
  /**
   * Set by `BriefSectionShell` when the section is retired. Without it the shell's
   * `cloneElement(child, { disabled: true })` landed on a component that did not read the
   * prop, so a retired prose section stayed editable behind its retired badge.
   */
  disabled?: boolean;
  /**
   * The page's pinned last-focused editor. When it is this section's own instance, the section
   * shows the format bar. Identity comparison, not DOM focus: the pin deliberately survives
   * blur, because clicking a bar button takes focus off the prose.
   */
  activeEditor?: Editor | null;
  /** Shown while the section is empty; the page passes the section's own description. */
  placeholder?: string;
}

const EMPTY_ANNOTATIONS: BriefCommentAnnotation[] = [];

export function BriefSectionEditor({
  section,
  onChange,
  onEditorFocus,
  registerEditor,
  onAddCommentInitiated,
  onHighlightClick,
  annotations = EMPTY_ANNOTATIONS,
  disabled = false,
  activeEditor = null,
  placeholder,
}: BriefSectionEditorProps) {
  const { pendingTextAnchor, setPendingTextAnchor, clearPendingTextAnchor } =
    usePendingTextAnchor();
  const pendingAnchorRef = useRef(pendingTextAnchor);
  useEffect(() => {
    pendingAnchorRef.current = pendingTextAnchor;
  }, [pendingTextAnchor]);

  // A pending selection in this section drives this editor through its callbacks, and it can
  // outlive the rail closing and reopening; it must not outlive the editor itself.
  useMountEffect(() => () => {
    if (pendingAnchorRef.current?.sectionId === section.id) clearPendingTextAnchor();
  });

  const selectedCommentId = useSelectedCommentId();
  const { selectComment, clearSelectedComment } = useSelectionActions();
  const focusIdRef = useRef(selectedCommentId);
  useEffect(() => {
    focusIdRef.current = selectedCommentId;
  }, [selectedCommentId]);

  // Clicking highlighted text cycles selection through the comments overlapping
  // that spot: not-selected → first; last → none; otherwise → next.
  const handleCommentClick = useCallback(
    (commentIds: string[]) => {
      if (commentIds.length === 0) return;
      const current = focusIdRef.current;
      const idx = current ? commentIds.indexOf(current) : -1;
      const next =
        idx === -1 ? commentIds[0] : idx === commentIds.length - 1 ? null : commentIds[idx + 1];
      if (next) selectComment(next);
      else clearSelectedComment();
      onHighlightClick?.();
    },
    [selectComment, clearSelectedComment, onHighlightClick],
  );

  const { canUploadAssets, canEditDetails } = useProjectCapabilities();
  const shouldReduceMotion = useReducedMotion();
  const editor = useBriefSectionViewer(section.content, {
    editable: !disabled && (canUploadAssets || canEditDetails),
    // Highlights are decorations, never in content — emit the doc as-is.
    onUpdate: ({ editor: instance }) => onChange(section.id, instance.getJSON()),
    onFocus: onEditorFocus ? ({ editor: instance }) => onEditorFocus(instance) : undefined,
    onCommentClick: handleCommentClick,
    placeholder,
    ariaLabel: section.name,
  });

  // Editor registry + highlight bridge — both are "install while this editor
  // instance is alive for this sectionId, tear down when it isn't". Setup and
  // teardown are the two halves of one symmetric, post-commit unit, so both
  // ride the section container's callback ref (React 19 returns the cleanup on
  // detach). Registering during render instead would leave the section
  // permanently unregistered whenever the node detaches and re-attaches
  // without a re-render — exactly what StrictMode's double ref invocation
  // does on mount — silently breaking apply-agent-suggestion and highlights.
  const sectionId = section.id;
  const attachSectionRoot = useCallback(
    (el: HTMLElement | null) => {
      if (!el || !editor || !sectionId) return;
      registerEditor?.(sectionId, editor);
      briefSectionHighlights.set(sectionId, {
        setDraft: (range) => editor.commands.setHighlightDraft(range),
        confirmDraft: (commentId) => editor.commands.confirmHighlightDraft(commentId),
        upsert: (highlight) => editor.commands.upsertCommentHighlight(highlight),
        remove: (commentId) => editor.commands.removeCommentHighlight(commentId),
        readRanges: () => getCommentHighlightRanges(editor),
      });
      return () => {
        registerEditor?.(sectionId, null);
        briefSectionHighlights.delete(sectionId);
      };
    },
    [editor, sectionId, registerEditor],
  );

  // Hydrate this section's highlights from the comment annotations. The plugin
  // maps them through edits, so we only re-seed when the annotations change.
  useEffect(() => {
    if (!editor) return;
    const forSection = annotations
      .filter((a) => a.annotation.sectionId === section.id)
      .map((a) => ({ commentId: a.commentId, from: a.annotation.from, to: a.annotation.to }));
    editor.commands.setCommentHighlights(forSection);
  }, [editor, annotations, section.id]);

  useEffect(() => {
    if (!editor) return;
    editor.commands.setFocusedCommentHighlight(selectedCommentId);
  }, [editor, selectedCommentId]);

  useEffect(() => {
    if (!editor || !selectedCommentId) return;
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- querySelector on Element is valid; deprecated notice is a TS DOM lib quirk
    const markEl = editor.view.dom.querySelector(`[data-comment-id="${selectedCommentId}"]`);
    // Smooth scrolling was unconditional here, which ignored a reader asking for less motion.
    markEl?.scrollIntoView({
      behavior: shouldReduceMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  }, [editor, selectedCommentId, shouldReduceMotion]);

  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    const capturedEditor = editor;
    const sectionId = section.id;
    const briefId = section.briefId;

    function onMouseUp() {
      const { from, to } = capturedEditor.state.selection;
      if (from === to) {
        pendingAnchorRef.current?.cancelDraft();
        return;
      }
      pendingAnchorRef.current?.cancelDraft();
      // Overlap is allowed — the draft is a decoration that stacks over any
      // existing highlights.
      capturedEditor.commands.setHighlightDraft({ from, to });
      const selectedText = capturedEditor.state.doc.textBetween(from, to, ' ');
      setPendingTextAnchor({
        sectionId,
        // While a comment is being edited, a new selection moves that comment's highlight.
        targetId: getEditingCommentId() === null ? briefId : null,
        from,
        to,
        selectedText,
        applyMark: (commentId: string) => {
          capturedEditor.commands.confirmHighlightDraft(commentId);
          clearPendingTextAnchor();
        },
        cancelDraft: () => {
          capturedEditor.commands.setHighlightDraft(null);
          clearPendingTextAnchor();
        },
      });
      onAddCommentInitiated?.();
    }

    dom.addEventListener('mouseup', onMouseUp);
    return () => dom.removeEventListener('mouseup', onMouseUp);
  }, [
    editor,
    section.id,
    section.briefId,
    setPendingTextAnchor,
    clearPendingTextAnchor,
    onAddCommentInitiated,
  ]);

  if (!editor) {
    return <BriefSectionError name={section.name} />;
  }

  // The name, the landmark and the permission gate all live in `BriefSectionShell` now, which
  // wraps prose and widgets alike. What is left here is the prose surface itself: a text
  // target that bleeds slightly wider than the column so the hit area reaches past the text.
  //
  // No min-height: each section hugs its content, so the rhythm reads identically whether a
  // section is a one-liner or a long list. ProseMirror always renders at least one empty
  // paragraph, so an empty section still keeps a clickable line.
  return (
    <div
      ref={attachSectionRoot}
      data-testid="brief-section-editor"
      className="-mx-xs rounded-md p-xs"
    >
      {activeEditor === editor && !disabled && <BriefSectionFormatBar editor={editor} />}
      <EditorContent editor={editor} className={BRIEF_PROSE_CLASSES} />
    </div>
  );
}
